import { useRef, useState, useMemo, useEffect } from 'react';
import { getDefaultExperimentState, fetchExperiments } from '../../utils/abeagle.js';

/**
 * sets up the initial experiment context
 * @param {String} userId - individual identifier for the context
 * @param {Object} data - Memoized page context to use in verifying experiment eligibility
 * @param {ExperimentConfigs} experimentConfig - constant - declared experiments for the application
 * @param {String} source - service ID, e.g. 'buzz_web' or 'tasty_web'
 * @param {String} abeagleHost - the domain to call for experiments
 * @param {Function} trackFn - optional function to send events
 * @returns {ExperimentState} experimentState - eligible, declared, and returned experiments
 */
function useABeagleExperiments(_ref) {
  let {
    userId,
    data,
    experimentConfig,
    source,
    abeagleHost,
    trackFn
  } = _ref;
  /**
   * check for all required params
   */
  const requiredFields = {
    data,
    experimentConfig,
    source,
    abeagleHost
  };
  const missingFields = Object.keys(requiredFields).reduce((acc, key) => {
    if (!requiredFields[key]) {
      acc.push(key);
    }
    return acc;
  }, []);
  if (missingFields.length) {
    throw new Error(`Missing required fields: ${missingFields.join(', ')}`);
  }

  /**
   * Will contain all experiments from experiment config, regardless
   *   of eligibility for any given page (data)
   */
  const allExperiments = useRef({
    ...getDefaultExperimentState()
  });
  const [loaded, setLoaded] = useState(false);
  const experimentState = useMemo(() => ({
    ...allExperiments.current,
    loaded
  }), [loaded]);

  /**
   * Should only get called once per app load, when userId has been set
   */
  useEffect(() => {
    if (userId) {
      setLoaded(false);
      (async () => {
        allExperiments.current = await fetchExperiments({
          userId,
          data,
          experimentConfig,
          source,
          abeagleHost,
          trackFn
        });
        setLoaded(true);
      })();
    }
  }, [userId, data]);
  return experimentState;
}

export { useABeagleExperiments as default };
